import React, {useEffect, useState} from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const STATUS_STYLES = {
  pass: {Icon: CheckCircleIcon, text: 'text-green-600 dark:text-green-400'},
  fail: {Icon: XCircleIcon, text: 'text-red-600 dark:text-red-400'},
  warn: {Icon: ExclamationTriangleIcon, text: 'text-yellow-600 dark:text-yellow-400'},
  skip: {Icon: MinusCircleIcon, text: 'text-gray-400 dark:text-gray-500'},
};

/**
 * Superuser-only pre-payout checklist. Pure props component:
 * - validations: slice state {status, data, error, forKey}
 * - viewKey: `${resourceId}|${year}|${Number(month)}` currently viewed
 * - onRun: () => void  (dispatches the fetch for the viewed month)
 * - periodLabel: human label, e.g. "June 2026"
 */
export default function PaymentValidationPanel({validations, viewKey, onRun, periodLabel}) {
  const {status, data, error, forKey} = validations || {};
  const current = status === 'succeeded' && forKey === viewKey && Array.isArray(data?.checks) && data?.summary ? data : null;
  const loading = status === 'loading';
  const failed = status === 'failed' && forKey === viewKey;
  const hasIssues = !!current && (current.summary.fail > 0 || current.summary.warn > 0);

  // Folded by default when every check passes; issues auto-expand.
  // Re-derive whenever a new result set (or a state change) arrives.
  const [expanded, setExpanded] = useState(hasIssues);
  useEffect(() => {
    setExpanded(hasIssues);
  }, [hasIssues, forKey, status]);

  if (!viewKey) return null;
  // Loading / error / not-run bodies are single lines; only successful
  // results are foldable.
  const showBody = !current || expanded;

  const runButton = (
    <button
      type="button"
      onClick={onRun}
      disabled={loading}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
    >
      <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
      Run validations
    </button>
  );

  return (
    <div className="bg-surface dark:bg-gray-870 rounded-lg border border-gray-200/70 dark:border-gray-600/70 shadow-sm">
      <div className={`flex items-center justify-between px-5 py-3 ${showBody ? 'border-b border-gray-100 dark:border-gray-700' : ''}`}>
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          className="flex items-center gap-2 min-w-0 text-left"
        >
          <ShieldCheckIcon className="w-5 h-5 text-brand-800 dark:text-brand-300 flex-shrink-0" aria-hidden="true" />
          <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
            Payment validations - {periodLabel}
          </span>
          {current && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {current.summary.pass} passed
              {current.summary.fail > 0 && ` · ${current.summary.fail} failed`}
              {current.summary.warn > 0 && ` · ${current.summary.warn} warnings`}
              {current.summary.skip > 0 && ` · ${current.summary.skip} skipped`}
            </span>
          )}
          {current && (
            <ChevronDownIcon
              className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          )}
        </button>
        {runButton}
      </div>
      {showBody && (
      <div className="px-5 py-3">
        {loading && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Running checks…</p>
        )}
        {failed && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" aria-hidden="true" />
            <p className="text-sm text-red-600 dark:text-red-400">
              Validation request failed: {String(error)}. Use "Run validations" to retry.
            </p>
          </div>
        )}
        {!loading && !failed && !current && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Validations have not been run for this month yet.
          </p>
        )}
        {!loading && current && (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {current.checks.map((check) => {
              const {Icon, text} = STATUS_STYLES[check.status] || STATUS_STYLES.skip;
              const expandable = check.details && check.details.length > 0
                && (check.status === 'fail' || check.status === 'warn');
              return (
                <li key={check.id} className="py-2">
                  <div className="flex items-start gap-2">
                    <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${text}`} aria-hidden="true" />
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                        {check.label}
                      </span>
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        {check.message}
                      </span>
                      {expandable && (
                        <details className="mt-1">
                          <summary className="text-xs cursor-pointer text-gray-500 dark:text-gray-400">
                            Details ({check.details.length})
                          </summary>
                          <ul className="mt-1 pl-4 list-disc space-y-0.5">
                            {check.details.map((d, i) => (
                              <li key={i} className="text-xs font-mono text-gray-600 dark:text-gray-300">{d}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      )}
    </div>
  );
}
